/* eslint-disable react/no-array-index-key */
import { useResizeObserver } from '@folklore/hooks';
import classNames from 'classnames';
import { camelCase, isString } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';

import styles from '../../../styles/animations/symbols.module.scss';

const propTypes = {
    images: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
    animation: PropTypes.oneOf(['rain-down', 'appear', 'rain-up']),
    maxDelay: PropTypes.number,
    minDuration: PropTypes.number,
    maxDuration: PropTypes.number,
    minScale: PropTypes.number,
    maxScale: PropTypes.number,
    size: PropTypes.number,
    coverage: PropTypes.number,
    className: PropTypes.string,
    onAnimationComplete: PropTypes.func,
};

const defaultProps = {
    animation: null,
    maxDelay: 0.5,
    minDuration: 1,
    maxDuration: 2,
    minScale: 0.5,
    maxScale: 2,
    size: 40,
    coverage: 0.9,
    className: null,
    onAnimationComplete: null,
};

function Symbols({
    images,
    animation,
    maxDelay,
    minDuration,
    maxDuration,
    minScale,
    maxScale,
    size,
    coverage,
    className,
    onAnimationComplete,
}) {
    const finalImages = useMemo(() => (isString(images) ? [images] : images), [images]);
    const {
        ref,
        entry: { contentRect },
    } = useResizeObserver();
    const { width = 0, height = 0 } = contentRect || {};
    const totalRows = Math.floor(height / size);
    const totalCols = Math.floor(width / size);
    const totalCount = Math.round(totalRows * totalCols * coverage);
    const rows = Math.ceil(totalRows * (coverage * 2));
    const cols = Math.ceil(totalCount / rows);
    const cellWidth = width / cols;
    const cellHeight = width / rows;

    const symbols = useMemo(() => {
        const data = [...Array(totalCount)].map((val, index) => {
            const finalCols = cols + 1;
            const row = Math.floor(index / finalCols);
            const col = index % finalCols;
            const offsetX = Math.random() * size - size / 2;
            const offsetY = Math.random() * size - size / 2;
            return {
                image: finalImages[Math.floor(Math.random() * finalImages.length)],
                x: col * cellWidth + offsetX,
                y: row * cellHeight + offsetY,
                scale: Math.random() * (maxScale - minScale) + minScale,
                delay: Math.random() * maxDelay,
                duration: Math.random() * (maxDuration - minDuration) + minDuration,
            };
        });
        return data;
    }, [
        finalImages,
        cols,
        totalCount,
        cellWidth,
        cellHeight,
        size,
        maxDelay,
        minDuration,
        maxDuration,
        minScale,
        maxScale,
    ]);

    const totalDuration = useMemo(
        () =>
            symbols.reduce((total, { delay, duration }) => {
                const currentTotal = delay + duration;
                return currentTotal > total ? currentTotal : total;
            }, 0),
        [symbols],
    );

    useEffect(() => {
        if (animation !== null) {
            setTimeout(() => {
                if (onAnimationComplete !== null) {
                    onAnimationComplete();
                }
            }, totalDuration * 1000);
        }
    }, [animation, onAnimationComplete, totalDuration]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles[animation !== null ? camelCase(animation) : null]]: animation !== null,
                    [className]: className !== null,
                },
            ])}
            ref={ref}
        >
            {symbols.map(({ image, delay, duration, scale, x, y }, symbolIndex) => (
                <div
                    key={`symbol-${symbolIndex}`}
                    className={styles.symbol}
                    style={{
                        top: y,
                        left: x,
                        animationDelay: `${delay}s`,
                        animationDuration: `${duration}s`,
                    }}
                >
                    <img
                        className={styles.image}
                        src={image}
                        alt="Symbol"
                        style={{
                            width: size * scale,
                        }}
                    />
                </div>
            ))}
        </div>
    );
}

Symbols.propTypes = propTypes;
Symbols.defaultProps = defaultProps;

export default Symbols;
